import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionHeader, StatBlock, StatusBadge } from "../../../components/control";
import {
  bccCreateOperationConfirmationCallable,
  bccGrantEmergencyCreditsCallable,
  bccListCreditPackCatalogCallable,
  bccListOffersCallable,
  bccProcessTaskEngineCallable,
  bccQueryAuditLogCallable,
  bccRunBillingReconciliationCallable,
  bccRunTaskAutomationSweepCallable,
  bccUpsertCreditPackCatalogItemCallable,
  type BccAuditEvent,
  type BccDashboardData,
  type BccOfferListItem,
} from "../../../lib/bccPhase1Callables";
import {
  adminGetCommercialSettingsCallable,
  adminGetControlTowerFinancialsCallable,
  adminGetControlTowerHealthCallable,
  adminGetControlTowerTelemetryCallable,
  adminGetOperatorAccessPolicyCallable,
  adminSetOperatorAccessPolicyCallable,
  adminUpsertCommercialSettingsCallable,
  type ControlTowerFinancialsPayload,
  type ControlTowerTelemetryPayload,
} from "../../../lib/adminControlTowerCallables";

const AED = (value: number) => `AED ${Math.round(value).toLocaleString()}`;
const pct = (value: number) => `${Number.isFinite(value) ? value.toFixed(1) : "0.0"}%`;

function ErrorBanner({ error }: { error: string }) {
  if (!error) return null;
  return <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>;
}

function EmptyState({ children }: { children: string }) {
  return <p className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm font-semibold text-slate-400">{children}</p>;
}

function DataTable({
  columns,
  children,
}: {
  columns: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="grid gap-3 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[11px] font-black uppercase tracking-wide text-slate-400" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
        {columns.map((column) => <span key={column}>{column}</span>)}
      </div>
      {children}
    </div>
  );
}

function TableRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid gap-3 border-b border-slate-50 px-4 py-3 text-sm last:border-b-0" style={{ gridTemplateColumns: `repeat(${Array.isArray(children) ? children.length : 1}, minmax(0, 1fr))` }}>
      {children}
    </div>
  );
}

function useAsyncLoad<T>(loader: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const next = await loader();
      setData(next);
      setUpdatedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this Control Center surface.");
    } finally {
      setLoading(false);
    }
  }, [loader]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, updatedAt, loading, error, load, setData, setError };
}

export function ControlWorkQueuePage() {
  const [status, setStatus] = useState("");
  const dashboard = useAsyncLoad<BccDashboardData>(async () => {
    const { bccGetDashboardCallable } = await import("../../../lib/bccPhase1Callables");
    return (await bccGetDashboardCallable({})).data;
  });

  const runSweep = async () => {
    setStatus("Running task automation sweep...");
    const result = await bccRunTaskAutomationSweepCallable({});
    await dashboard.load();
    setStatus(`Sweep complete: ${result.data.createdTasks} tasks created.`);
  };

  const runEngine = async () => {
    setStatus("Processing task engine...");
    const result = await bccProcessTaskEngineCallable({ dryRun: false });
    await dashboard.load();
    setStatus(`Checked ${result.data.checked}; escalated ${result.data.escalated}; auto-resolved ${result.data.autoResolved}.`);
  };

  const cards = dashboard.data?.cards;
  return (
    <div className="space-y-5">
      <SectionHeader title="Work Queue" subtitle="Operator tasks and platform alerts." updatedAt={dashboard.updatedAt} onRefresh={() => void dashboard.load()} refreshing={dashboard.loading} />
      <ErrorBanner error={dashboard.error} />
      {status ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{status}</div> : null}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatBlock label="Open support" value={cards?.openSupportIssues ?? "—"} />
        <StatBlock label="Payment failed" value={cards?.paymentFailed ?? "—"} />
        <StatBlock label="Pending proposals" value={cards?.pendingProposals ?? "—"} />
        <StatBlock label="Low credit tenants" value={cards?.lowCreditTenants ?? "—"} />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <button type="button" onClick={() => void runSweep()} className="rounded-xl border border-slate-200 bg-white px-4 py-4 text-left shadow-sm hover:border-orange-300">
          <p className="text-sm font-black text-slate-950">Run automation sweep</p>
          <p className="mt-1 text-xs text-slate-500">Creates missing operator tasks for lifecycle and billing risk.</p>
        </button>
        <button type="button" onClick={() => void runEngine()} className="rounded-xl border border-slate-200 bg-white px-4 py-4 text-left shadow-sm hover:border-orange-300">
          <p className="text-sm font-black text-slate-950">Process task engine</p>
          <p className="mt-1 text-xs text-slate-500">Escalates stale tasks and auto-resolves completed work.</p>
        </button>
      </div>
    </div>
  );
}

export function ControlOffersPage() {
  const offers = useAsyncLoad<BccOfferListItem[]>(async () => (await bccListOffersCallable({ limit: 100 })).data.offers ?? []);
  return (
    <div className="space-y-5">
      <SectionHeader
        title="Offers"
        subtitle="Demo and subscription proposals, margin-gated."
        updatedAt={offers.updatedAt}
        onRefresh={() => void offers.load()}
        refreshing={offers.loading}
        actions={<Link to="/control/offers/new" className="rounded-xl bg-slate-950 px-3 py-1.5 text-xs font-bold text-white">New offer</Link>}
      />
      <ErrorBanner error={offers.error} />
      {!offers.data?.length ? <EmptyState>No offers found.</EmptyState> : (
        <DataTable columns={["Tenant", "Type", "Status", "Monthly", "Margin", "Updated"]}>
          {offers.data.map((offer) => (
            <TableRow key={offer.offerId}>
              <span className="truncate font-bold text-slate-900">{offer.tenantId}</span>
              <span className="capitalize text-slate-600">{offer.offerType}</span>
              <span><StatusBadge state={offer.status} /></span>
              <span className="font-mono text-slate-700">{AED(offer.finalMonthlyPriceAed)}</span>
              <span className={`font-mono font-bold ${offer.grossMarginPercent < 10 ? "text-orange-600" : "text-emerald-700"}`}>{pct(offer.grossMarginPercent)}</span>
              <span className="text-slate-400">{offer.updatedAt ? new Date(offer.updatedAt).toLocaleDateString() : "—"}</span>
            </TableRow>
          ))}
        </DataTable>
      )}
    </div>
  );
}

export function ControlRevenuePage() {
  const financials = useAsyncLoad<ControlTowerFinancialsPayload>(async () => (await adminGetControlTowerFinancialsCallable({})).data);
  const rows = financials.data?.rows ?? [];
  const totals = useMemo(() => rows.reduce((acc, row) => ({
    revenue: acc.revenue + row.stripeAmount + row.creditSales,
    apiCost: acc.apiCost + row.apiCost,
    balance: acc.balance + row.balance,
  }), { revenue: 0, apiCost: 0, balance: 0 }), [rows]);
  return (
    <div className="space-y-5">
      <SectionHeader title="Revenue" subtitle="Subscription, credit-pack revenue, and estimated AI cost." updatedAt={financials.updatedAt} onRefresh={() => void financials.load()} refreshing={financials.loading} />
      <ErrorBanner error={financials.error} />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <StatBlock label="Total revenue" value={AED(totals.revenue)} />
        <StatBlock label="Estimated AI cost" value={AED(totals.apiCost)} />
        <StatBlock label="Net contribution" value={AED(totals.revenue - totals.apiCost)} />
        <StatBlock label="Ledger credits" value={totals.balance.toLocaleString()} />
      </div>
      {!rows.length ? <EmptyState>No financial rows available.</EmptyState> : (
        <DataTable columns={["Tenant", "Revenue", "AI cost", "Profit", "Burn / day", "Balance"]}>
          {rows.slice(0, 50).map((row) => {
            const revenue = row.stripeAmount + row.creditSales;
            return (
              <TableRow key={row.id}>
                <span className="truncate font-bold text-slate-900">{row.firmName}</span>
                <span>{AED(revenue)}</span>
                <span>{AED(row.apiCost)}</span>
                <span className={revenue - row.apiCost < 0 ? "font-bold text-red-600" : "font-bold text-emerald-700"}>{AED(revenue - row.apiCost)}</span>
                <span>{row.burnRate7d.toFixed(1)}</span>
                <span>{row.balance.toLocaleString()}</span>
              </TableRow>
            );
          })}
        </DataTable>
      )}
    </div>
  );
}

export function ControlBillingPage() {
  const [status, setStatus] = useState("");
  const financials = useAsyncLoad<ControlTowerFinancialsPayload>(async () => (await adminGetControlTowerFinancialsCallable({})).data);
  const runReconcile = async () => {
    setStatus("Running Stripe reconciliation...");
    const result = await bccRunBillingReconciliationCallable({ limit: 100 });
    await financials.load();
    setStatus(`Checked ${result.data.checked}; mismatches ${result.data.mismatches}; alerts ${result.data.alertsCreated}.`);
  };
  const risky = (financials.data?.rows ?? []).filter((row) => row.stripeAmount === 0 || row.apiCost > row.stripeAmount + row.creditSales);
  return (
    <div className="space-y-5">
      <SectionHeader title="Billing" subtitle="Stripe reconciliation, payment failures, and recovery posture." updatedAt={financials.updatedAt} onRefresh={() => void financials.load()} refreshing={financials.loading} actions={<button type="button" onClick={() => void runReconcile()} className="rounded-xl bg-slate-950 px-3 py-1.5 text-xs font-bold text-white">Run reconciliation</button>} />
      <ErrorBanner error={financials.error} />
      {status ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{status}</div> : null}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatBlock label="Tenants checked" value={financials.data?.rows.length ?? "—"} />
        <StatBlock label="Billing risk rows" value={risky.length} />
        <StatBlock label="Total credit sales" value={AED((financials.data?.rows ?? []).reduce((sum, row) => sum + row.creditSales, 0))} />
      </div>
      {!risky.length ? <EmptyState>No billing risk rows in the latest financial scan.</EmptyState> : (
        <DataTable columns={["Tenant", "Stripe", "Credits", "AI cost", "Risk"]}>
          {risky.slice(0, 40).map((row) => (
            <TableRow key={row.id}>
              <span className="truncate font-bold text-slate-900">{row.firmName}</span>
              <span>{AED(row.stripeAmount)}</span>
              <span>{AED(row.creditSales)}</span>
              <span>{AED(row.apiCost)}</span>
              <span className="font-bold text-orange-600">{row.stripeAmount === 0 ? "No Stripe revenue" : "Cost above revenue"}</span>
            </TableRow>
          ))}
        </DataTable>
      )}
    </div>
  );
}

export function ControlAiCreditsPage() {
  const catalog = useAsyncLoad(async () => (await bccListCreditPackCatalogCallable({ limit: 50 })).data.packs ?? []);
  const financials = useAsyncLoad<ControlTowerFinancialsPayload>(async () => (await adminGetControlTowerFinancialsCallable({})).data);
  const [grantFirmId, setGrantFirmId] = useState("");
  const [grantAmount, setGrantAmount] = useState(250);
  const [status, setStatus] = useState("");
  const grantCredits = async () => {
    if (!grantFirmId.trim() || grantAmount <= 0) return;
    setStatus("Creating confirmation...");
    const confirmation = await bccCreateOperationConfirmationCallable({
      operationType: "emergency_credit_grant",
      payload: { targetFirmId: grantFirmId.trim(), amount: grantAmount },
      reason: "Control Center AI Credits grant",
    });
    const result = await bccGrantEmergencyCreditsCallable({
      targetFirmId: grantFirmId.trim(),
      amount: grantAmount,
      reason: "Control Center AI Credits grant",
      confirmationToken: confirmation.data.confirmationToken,
    });
    await financials.load();
    setStatus(`Granted ${result.data.amount} credits. New balance: ${result.data.newBalance}.`);
    setGrantFirmId("");
  };
  const lowCredit = (financials.data?.rows ?? []).filter((row) => row.balance <= 100);
  return (
    <div className="space-y-5">
      <SectionHeader title="AI Credits" subtitle="Balances, burn, pack catalog, and controlled grants." updatedAt={financials.updatedAt ?? catalog.updatedAt} onRefresh={() => { void catalog.load(); void financials.load(); }} refreshing={financials.loading || catalog.loading} />
      <ErrorBanner error={financials.error || catalog.error} />
      {status ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{status}</div> : null}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatBlock label="Low-credit tenants" value={lowCredit.length} />
        <StatBlock label="Active packs" value={catalog.data?.filter((pack) => pack.active).length ?? "—"} />
        <StatBlock label="Total balances" value={(financials.data?.rows ?? []).reduce((sum, row) => sum + row.balance, 0).toLocaleString()} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm font-black text-slate-950">Emergency credit grant</p>
        <div className="mt-3 grid gap-2 md:grid-cols-[1fr_160px_auto]">
          <input value={grantFirmId} onChange={(event) => setGrantFirmId(event.target.value)} placeholder="Tenant firm ID" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <input type="number" value={grantAmount} onChange={(event) => setGrantAmount(Number(event.target.value) || 0)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <button type="button" onClick={() => void grantCredits()} className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white">Grant</button>
        </div>
      </div>
      <DataTable columns={["Pack", "Price", "Credits", "Margin", "Active"]}>
        {(catalog.data ?? []).map((pack) => (
          <TableRow key={pack.packCode}>
            <span className="font-bold text-slate-900">{pack.packName}</span>
            <span>{AED(pack.packPriceAed)}</span>
            <span>{pack.includedCredits.toLocaleString()}</span>
            <span>{pct(pack.estimatedMarginPercent)}</span>
            <span>{pack.active ? "Yes" : "No"}</span>
          </TableRow>
        ))}
      </DataTable>
    </div>
  );
}

export function ControlPlatformHealthPage() {
  const telemetry = useAsyncLoad<ControlTowerTelemetryPayload>(async () => (await adminGetControlTowerTelemetryCallable({})).data);
  const health = useAsyncLoad(async () => (await adminGetControlTowerHealthCallable({})).data);
  return (
    <div className="space-y-5">
      <SectionHeader title="Platform Health" subtitle="Infra cost, webhook/job status, optional feed diagnostics." updatedAt={telemetry.updatedAt ?? health.updatedAt} onRefresh={() => { void telemetry.load(); void health.load(); }} refreshing={telemetry.loading || health.loading} />
      <ErrorBanner error={telemetry.error || health.error} />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <StatBlock label="Health" value={health.data?.ok ? "OK" : "Check"} />
        <StatBlock label="Jobs" value={telemetry.data?.jobs.length ?? "—"} />
        <StatBlock label="AI events" value={telemetry.data?.events.length ?? "—"} />
        <StatBlock label="Diagnostics" value={(telemetry.data?.diagnostics?.length ?? 0) + (health.data?.checks.filter((check) => check.status !== "pass").length ?? 0)} />
      </div>
      <DataTable columns={["Check", "Status", "Message"]}>
        {(health.data?.checks ?? []).map((check) => (
          <TableRow key={check.id}>
            <span className="font-bold text-slate-900">{check.id}</span>
            <span className={check.status === "pass" ? "font-bold text-emerald-700" : "font-bold text-orange-600"}>{check.status}</span>
            <span className="truncate text-slate-500">{check.message}</span>
          </TableRow>
        ))}
      </DataTable>
    </div>
  );
}

export function ControlSupportPage() {
  const dashboard = useAsyncLoad<BccDashboardData>(async () => {
    const { bccGetDashboardCallable } = await import("../../../lib/bccPhase1Callables");
    return (await bccGetDashboardCallable({})).data;
  });
  return (
    <div className="space-y-5">
      <SectionHeader title="Support" subtitle="Tickets, operator notes, escalations." updatedAt={dashboard.updatedAt} onRefresh={() => void dashboard.load()} refreshing={dashboard.loading} />
      <ErrorBanner error={dashboard.error} />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatBlock label="Open support issues" value={dashboard.data?.cards.openSupportIssues ?? "—"} />
        <StatBlock label="Payment-failed tenants" value={dashboard.data?.cards.paymentFailed ?? "—"} />
        <StatBlock label="Low-credit tenants" value={dashboard.data?.cards.lowCreditTenants ?? "—"} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm font-black text-slate-950">Support workflow</p>
        <p className="mt-2 text-sm text-slate-500">Open a tenant from the Tenants list to review tenant-specific support tickets, audit, and communications.</p>
        <Link to="/control/tenants" className="mt-3 inline-flex rounded-xl bg-slate-950 px-3 py-2 text-sm font-bold text-white">Open tenants</Link>
      </div>
    </div>
  );
}

export function ControlAuditPage() {
  const audit = useAsyncLoad<BccAuditEvent[]>(async () => (await bccQueryAuditLogCallable({ limit: 100 })).data.events ?? []);
  return (
    <div className="space-y-5">
      <SectionHeader title="Audit" subtitle="Unified audit stream of operator and lifecycle action." updatedAt={audit.updatedAt} onRefresh={() => void audit.load()} refreshing={audit.loading} />
      <ErrorBanner error={audit.error} />
      {!audit.data?.length ? <EmptyState>No audit events found.</EmptyState> : (
        <DataTable columns={["Event", "Target", "Actor", "Reason", "Created"]}>
          {audit.data.map((event) => (
            <TableRow key={event.eventId}>
              <span className="font-bold text-slate-900">{event.eventType}</span>
              <span className="truncate text-slate-600">{event.targetType}:{event.targetId}</span>
              <span className="truncate text-slate-500">{event.actorEmail || event.actorUid}</span>
              <span className="truncate text-slate-500">{event.reason || "—"}</span>
              <span className="text-slate-400">{event.createdAt ? new Date(event.createdAt).toLocaleString() : "—"}</span>
            </TableRow>
          ))}
        </DataTable>
      )}
    </div>
  );
}

export function ControlSettingsPage() {
  const [status, setStatus] = useState("");
  const commercial = useAsyncLoad(async () => (await adminGetCommercialSettingsCallable({})).data);
  const policy = useAsyncLoad(async () => (await adminGetOperatorAccessPolicyCallable({})).data);
  const [packPrice, setPackPrice] = useState(500);
  const [packCode, setPackCode] = useState("starter_500");
  const savePolicy = async () => {
    if (!policy.data) return;
    const result = await adminSetOperatorAccessPolicyCallable({
      nonOwnerKillSwitch: policy.data.nonOwnerKillSwitch,
      operatorSessionTimeoutMinutes: policy.data.operatorSessionTimeoutMinutes,
      mandatoryMfa: policy.data.mandatoryMfa,
      reasonCode: "CONTROL_SETTINGS_POLICY_UPDATE",
    });
    policy.setData({ ...policy.data, ...result.data });
    setStatus("Operator policy saved.");
  };
  const saveCommercial = async () => {
    if (!commercial.data) return;
    await adminUpsertCommercialSettingsCallable({ ...commercial.data, reasonCode: "CONTROL_SETTINGS_COMMERCIAL_UPDATE" });
    await commercial.load();
    setStatus("Commercial settings saved.");
  };
  const upsertPack = async () => {
    const result = await bccUpsertCreditPackCatalogItemCallable({ packCode, packPriceAed: packPrice, reason: "Control Settings pack update" });
    setStatus(`Saved ${result.data.packName}: ${result.data.includedCredits.toLocaleString()} credits at ${pct(result.data.estimatedMarginPercent)} margin.`);
  };
  return (
    <div className="space-y-5">
      <SectionHeader title="Settings" subtitle="Catalog, operators, founding slots, and security." updatedAt={commercial.updatedAt ?? policy.updatedAt} onRefresh={() => { void commercial.load(); void policy.load(); }} refreshing={commercial.loading || policy.loading} />
      <ErrorBanner error={commercial.error || policy.error} />
      {status ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{status}</div> : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-black text-slate-950">Commercial defaults</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <label className="text-xs font-bold text-slate-500">Plan base AED<input type="number" value={commercial.data?.planBasePriceAed ?? 0} onChange={(e) => commercial.setData(commercial.data ? { ...commercial.data, planBasePriceAed: Number(e.target.value) || 0 } : null)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
            <label className="text-xs font-bold text-slate-500">Max discount %<input type="number" value={commercial.data?.maxDiscountPercent ?? 0} onChange={(e) => commercial.setData(commercial.data ? { ...commercial.data, maxDiscountPercent: Number(e.target.value) || 0 } : null)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
            <label className="text-xs font-bold text-slate-500">Monthly credits<input type="number" value={commercial.data?.defaultMonthlyIncludedCredits ?? 0} onChange={(e) => commercial.setData(commercial.data ? { ...commercial.data, defaultMonthlyIncludedCredits: Number(e.target.value) || 0 } : null)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
            <label className="text-xs font-bold text-slate-500">Founding slots<input type="number" value={commercial.data?.foundingSlotLimit ?? 0} onChange={(e) => commercial.setData(commercial.data ? { ...commercial.data, foundingSlotLimit: Number(e.target.value) || 0 } : null)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
          </div>
          <button type="button" onClick={() => void saveCommercial()} className="mt-4 rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white">Save commercial settings</button>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-black text-slate-950">Operator security</p>
          <div className="mt-3 space-y-3">
            <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">Mandatory MFA<input type="checkbox" checked={policy.data?.mandatoryMfa ?? true} onChange={(e) => policy.setData(policy.data ? { ...policy.data, mandatoryMfa: e.target.checked } : null)} /></label>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">Non-owner kill switch<input type="checkbox" checked={policy.data?.nonOwnerKillSwitch ?? false} onChange={(e) => policy.setData(policy.data ? { ...policy.data, nonOwnerKillSwitch: e.target.checked } : null)} /></label>
            <label className="text-xs font-bold text-slate-500">Session timeout minutes<input type="number" value={policy.data?.operatorSessionTimeoutMinutes ?? 60} onChange={(e) => policy.setData(policy.data ? { ...policy.data, operatorSessionTimeoutMinutes: Number(e.target.value) || 60 } : null)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></label>
          </div>
          <button type="button" onClick={() => void savePolicy()} className="mt-4 rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white">Save operator policy</button>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm font-black text-slate-950">Credit pack quick update</p>
        <div className="mt-3 grid gap-2 md:grid-cols-[1fr_180px_auto]">
          <input value={packCode} onChange={(e) => setPackCode(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <input type="number" value={packPrice} onChange={(e) => setPackPrice(Number(e.target.value) || 0)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <button type="button" onClick={() => void upsertPack()} className="rounded-xl bg-orange-600 px-4 py-2 text-sm font-bold text-white">Save pack</button>
        </div>
      </div>
    </div>
  );
}
